import { Box, Button, Link, Typography, useTheme } from "@mui/material";
import { PowershellIcon, MsiIcon } from "~/icons";
import { InstallationMethod } from "../types";
import { Config } from "~/constants/configuration_provider";

interface InstallationMethodSelectorProps {
  selectedMethod: InstallationMethod;
  onMethodChange: (method: InstallationMethod) => void;
}

export const InstallationMethodSelector = ({
  selectedMethod,
  onMethodChange,
}: InstallationMethodSelectorProps) => {
  const theme = useTheme();

  const buttonBaseStyles = {
    textTransform: "none" as const,
    borderRadius: 1,
    borderWidth: 1,
    borderStyle: "solid" as const,
    bgcolor: theme.palette.background.paper,
    px: 2,
    py: 1,
  };

  const isPowerShellSelected = selectedMethod === InstallationMethod.PowerShell;
  const isMsiSelected = selectedMethod === InstallationMethod.Msi;

  return (
    <Box sx={{ pl: 6, mb: 5 }}>
      <Box
        sx={{ display: "flex", gap: 2, mb: 2 }}
        role="group"
        aria-label="Installation method selection"
      >
        <Button
          variant={isPowerShellSelected ? "contained" : "outlined"}
          onClick={() => onMethodChange(InstallationMethod.PowerShell)}
          startIcon={<PowershellIcon />}
          aria-pressed={isPowerShellSelected}
          aria-label="Select PowerShell installation method"
          sx={{
            ...buttonBaseStyles,
            borderColor: isPowerShellSelected
              ? "primary.main"
              : "background.paper",
          }}
        >
          PowerShell
        </Button>
        <Button
          variant={isMsiSelected ? "contained" : "outlined"}
          onClick={() => onMethodChange(InstallationMethod.Msi)}
          startIcon={<MsiIcon />}
          aria-pressed={isMsiSelected}
          aria-label="Select Manual MSI Installation method"
          sx={{
            ...buttonBaseStyles,
            borderColor: isMsiSelected ? "primary.main" : "background.paper",
          }}
        >
          Manual MSI Installation
        </Button>
      </Box>

      {isMsiSelected && (
        <Typography variant="body1" color="text.secondary" sx={{ mb: 1 }}>
          Download the latest Mondoo package from{" "}
          <Link
            href={`https://${Config.VITE_RELEASES_HOST}/mondoo`}
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              color: "primary.light",
              textDecoration: "none",
              "&:hover": { textDecoration: "underline" },
            }}
          >
            {Config.VITE_RELEASES_HOST}
          </Link>
        </Typography>
      )}
    </Box>
  );
};
